import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core';

import Transition from '../../components/Dialogs/Transition';
import {
	add as addManualInput,
	update as updateManualInput,
} from '../../modules/reports/manualInput';

const styles = () => ({
	dialogPaper: {
		maxWidth: '100%',
		width: 480,
	},
	dialogPadding: {
		padding: '10px 25px 20px 25px',
	},
	row: {
		fontSize: '0.9rem',
		marginBottom: 6,
	},
	value: {
		fontWeight: 600,
	},
	history: {
		fontSize: '0.8rem',
		color: '#555',
		backgroundColor: '#fafafa',
		border: '1px solid #eee',
		borderRadius: 4,
		padding: '8px 12px',
		marginTop: 12,
	},
	historyTitle: {
		textTransform: 'uppercase',
		fontSize: 11,
		color: '#777',
		letterSpacing: 1,
		marginBottom: 4,
	},
	errorText: {
		color: '#c62828',
		fontSize: '0.85rem',
		marginTop: 8,
	},
});

function formatWhen(isoDate) {
	return moment(isoDate).format('M/D/YY h:mm a');
}

/**
 * Correct a hand entered value (Conway, DGS) for one day on the OPIS Pricing page, or add one
 * for a day that is missing it (pass missingDay instead of manualInput). Corrections keep the
 * original entry's date; the backend logs who added or changed a value and what it was.
 */
class EditManualInputDialog extends React.Component {
	state = {
		newValue: '',
		isSaving: false,
		error: null,
	};

	componentDidUpdate(prevProps) {
		//- Reset the form each time the dialog opens
		if (this.props.open && !prevProps.open) {
			this.setState({ newValue: '', isSaving: false, error: null });
		}
	}

	handleSave = () => {
		const { manualInput, missingDay, onSaved } = this.props;
		const newValue = _.trim(this.state.newValue);

		if (!newValue || !_.isFinite(Number(newValue))) {
			return this.setState({ error: 'Enter the value as a number' });
		}

		this.setState({ isSaving: true, error: null });
		const save = manualInput ? updateManualInput : addManualInput;
		const params = manualInput
			? { dataRecordId: manualInput.dataRecordId }
			: { fieldName: missingDay.fieldName, dayKey: missingDay.dayKey };
		save({
			...params,
			fieldValue: newValue,
			callback: (err, r) => {
				if (err) {
					const errors = _.isArray(err) ? err : [err];
					return this.setState({
						isSaving: false,
						error: errors.map((e) => (e && e.message) || String(e)).join(', '),
					});
				}
				this.setState({ isSaving: false });
				onSaved(r.manualInput);
			},
		});
	};

	render() {
		const { classes, open, onClose, manualInput, missingDay, display } = this.props;
		const { newValue, isSaving, error } = this.state;

		if (!manualInput && !missingDay) {
			return null;
		}
		const isAdding = !manualInput;
		const dayKey = isAdding ? missingDay.dayKey : manualInput.dayKey;
		const addedLater = !isAdding && _.find(manualInput.edits, { oldValue: null });
		let enteredLine = '';
		if (addedLater) {
			const who = addedLater.editedByName || 'unknown';
			enteredLine = `Added later by ${who} on ${formatWhen(addedLater.dateCreated)}`;
		} else if (!isAdding) {
			const who = manualInput.createdByName || 'unknown';
			enteredLine = `Entered by ${who} on ${formatWhen(manualInput.dateCreated)}`;
		}

		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				open={open}
				TransitionComponent={Transition}
				onClose={isSaving ? undefined : onClose}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>
						{isAdding ? 'Add' : 'Edit'} {display} for {moment(dayKey).format('ddd, M/D/YY')}
					</DialogTitle>
					<DialogContent>
						{isAdding && (
							<div className={classes.row}>
								No value was entered for this day. Your name will be recorded with the value you
								add.
							</div>
						)}
						{!isAdding && (
							<>
								<div className={classes.row}>
									Current value: <span className={classes.value}>{manualInput.fieldValue}</span>
								</div>
								<div className={classes.row}>{enteredLine}</div>
							</>
						)}

						<TextField
							label={isAdding ? display : `New ${display}`}
							value={newValue}
							fullWidth
							autoFocus
							style={{ marginTop: 12 }}
							onChange={(e) => this.setState({ newValue: e.target.value, error: null })}
							onKeyPress={(e) => {
								if (e.key === 'Enter' && !isSaving) {
									this.handleSave();
								}
							}}
						/>

						{error && <div className={classes.errorText}>{error}</div>}

						{!isAdding && !_.isEmpty(manualInput.edits) && (
							<div className={classes.history}>
								<div className={classes.historyTitle}>Edit history</div>
								{manualInput.edits.map((edit) => (
									<div key={edit.dataRecordEditId}>
										{formatWhen(edit.dateCreated)}:{' '}
										{edit.oldValue === null
											? `added ${edit.newValue}`
											: `${edit.oldValue} → ${edit.newValue}`}{' '}
										by {edit.editedByName || 'unknown'}
									</div>
								))}
							</div>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={this.handleSave}
							disabled={isSaving || !_.trim(newValue)}
						>
							{isSaving ? <CircularProgress size={20} /> : isAdding ? 'Add' : 'Save'}
						</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

EditManualInputDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	display: PropTypes.string,
	manualInput: PropTypes.object,
	missingDay: PropTypes.object,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
};

export default withStyles(styles)(EditManualInputDialog);
